// src/services/customerService.js
import { repo } from "../dbservice/repo";
import { CustomerModel } from "../models/CustomerModel";

class CustomerService {
  constructor() {
    this.collectionName = "customers";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[CustomerService] Failed to parse userData", e);
      return null;
    }
  }

  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.customerId || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      customerId: data.customerId || targetId,
      companyId: data.companyId || user?.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  async fetchCustomers({ companyId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }
    if (status) {
      filters.push({ field: "status", value: status });
    }

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[CustomerService] Failed to fetch customers:", error);
      return [];
    }
  }

  async fetchById(id) {
    if (!id) {
      console.error("[CustomerService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[CustomerService] Failed to fetch customer ${id}:`, error);
      return null;
    }
  }

  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Customer ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Customer ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new CustomerModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid customer payload: customerId, customerName, and a valid GSTIN format (if supplied) are required."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  async delete(id) {
    if (!id) throw new Error("Customer ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[CustomerService] Failed to delete customer ${id}:`, error);
      throw error;
    }
  }
}

export const customerService = new CustomerService();